import { useTranslations } from "../../../contexts/Translation";
import { CheckMark, ErrorIcon, Expand, Expanded } from "../../../components/Icons";
import { useApiData } from "../../../contexts/ApiData";
import { useOrganizations } from "../../../contexts/UserProvider";
import { Group, OrgUser } from "../../../types";
import { ReactElement, useEffect, useState } from "react";
import { useUserManagement } from "./UserManagementContext";
import GroupSelector from "./GroupSelector";
import { Select } from "../Input";
import Button from "../../Button/Button";
import ProfileImage from "@/components/ProfileImage";
import styles from "./UserItem.module.css";

const UserItem = ({ candidate, email, userId, role, department, image, groups: userGroupIds = [] }: OrgUser) => {
  const t = useTranslations("userManagementForm");
  const { put } = useApiData();
  const { currentOrganization } = useOrganizations();
  const { departments, groups: availableGroups } = useUserManagement();
  const [selectedRole, setSelectedRole] = useState<string>(role);
  const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(department);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [icon, setIcon] = useState<ReactElement | null>(null);

  useEffect(() => {
    const mapped = (userGroupIds || []).map((id) => availableGroups.find((g) => g.id === id)).filter(Boolean) as Group[];
    setSelectedGroups(mapped);
  }, [userGroupIds, availableGroups]);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = e.target.value;
    setSelectedRole(newRole);
  };

  const handleDepartmentChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDepartment = e.target.value;
    setSelectedDepartment(newDepartment);
  };

  const updateGroups = (selected: Group[]) => {
    setSelectedGroups(selected);
  };

  const updateUser = async () => {
    const updateRes = await put(`/organization/${currentOrganization}/users/${userId}`, {
      role: selectedRole,
      departmentId: selectedDepartment,
    });
    const groupIds = selectedGroups.map((g) => g.id);
    const groupsRes = await put(`/organization/${currentOrganization}/users/${userId}/groups`, {
      groups: groupIds,
    });

    setIcon(updateRes && groupsRes ? <CheckMark /> : <ErrorIcon />);
  };

  return (
    <>
      <div className={[styles.summary, isExpanded && styles.summaryExpanded].filter(Boolean).join(" ")} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.summaryInfo}>
          <ProfileImage src={image ? `/files/profile/${image}` : ""} alt={candidate || email} variant='small' />
          <div>
            {candidate} ({email})
          </div>
        </div>
        <div className={styles.summaryText}>{isExpanded ? <Expanded /> : <Expand />}</div>
      </div>
      <div className={[styles.details, !isExpanded && styles.hidden].filter(Boolean).join(" ")}>
        <Select
          options={[
            { value: "owner", text: t("roleOwner") },
            { value: "invited", text: t("roleInvited") },
            { value: "member", text: t("roleMember") },
            { value: "supporter", text: t("roleSupporter") },
          ]}
          label={t("role")}
          readOnly={role === "owner"}
          helpText={t("help.roleForUser")}
          name={`${candidate}_name`}
          handleChange={handleChange}
          value={`${selectedRole}`}
        />
        <Select
          name={`${candidate}_department`}
          label={t("department")}
          helpText={t("help.departmentForUser")}
          handleChange={handleDepartmentChange}
          value={selectedDepartment || ""}
          options={departments.map((d) => ({ value: d.id || "", text: d.name }))}
        />
        <GroupSelector helpText={t("help.groupsForUser")} groups={availableGroups} selected={selectedGroups} userId={userId} onChange={updateGroups} />
        <Button type='button' onClick={() => updateUser()}>
          {t("save")}
        </Button>
        {icon}
      </div>
    </>
  );
};

export default UserItem;
